import React from "react";

// Define interfaces for the component props
interface User {
  _id: string;
  fullName: string;
  email: string;
  role: "SUPERVISOR" | "DATA_COLLECTOR" | "ADMIN";
  employeeId?: string;
  createdAt: string;
}

interface Property {
  id: string;
  collectorId?: string;
  status?: string;
}

interface CollectorStats {
  assigned: number;
  inProgress: number;
  completed: number;
  pending: number;
}

interface DataCollectorsPerformanceProps {
  dataCollectors: User[];
  properties: Property[];
  collectorStats: Record<string, CollectorStats>;
}

const DataCollectorsPerformance: React.FC<DataCollectorsPerformanceProps> = ({
  dataCollectors,
  properties,
  collectorStats,
}) => {
  // Helper function to get user initials
  const getUserInitials = (fullName: string) => {
    if (!fullName) return "DC";
    return fullName
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mt-8">
      <h3 className="text-lg font-medium text-gray-800 mb-4">
        Data Collectors Performance
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Data Collector
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Employee ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Total Assigned
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                In Progress
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Completed
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Pending
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dataCollectors.map((collector, index) => {
              const stats = collectorStats[collector._id] || {
                assigned: 0,
                inProgress: 0,
                completed: 0,
                pending: 0,
              };
              const rowKey = collector._id
                ? `collector-${collector._id}`
                : `collector-index-${index}`;
              return (
                <tr key={rowKey}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold">
                          {getUserInitials(collector.fullName)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {collector.fullName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {collector.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {collector.employeeId || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {stats.assigned}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      {stats.inProgress}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {stats.completed}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      {stats.pending}
                    </span>
                  </td>
                </tr>
              );
            })}

            {/* Show unassigned properties if any */}
            {dataCollectors.length === 0 &&
              properties.some((p) => !p.collectorId) && (
                <tr key="unassigned-properties">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-semibold">
                          UA
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          Unassigned Properties
                        </div>
                        <div className="text-sm text-gray-500">
                          Properties not assigned to any collector
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    N/A
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {properties.filter((p) => !p.collectorId).length}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      0
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      0
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {
                        properties.filter(
                          (p) =>
                            !p.collectorId &&
                            (p.status === "PENDING" || p.status === "NEW"),
                        ).length
                      }
                    </span>
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>

      {dataCollectors.length === 0 &&
        properties.filter((p) => p.collectorId).length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No data collectors found. All properties are currently unassigned.
          </div>
        )}
    </div>
  );
};

export default DataCollectorsPerformance;
